import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'

import OrderSummaryCard from '../components/Orders/OrderSummaryCard'
import { PublicLink } from '../components/PublicLink'
import { useOrders } from '../hooks/useOrders'
import { theme } from '../theme'
import { hasShortDisplayVariant, shortenOrderNumber } from '../utils/orderNumber'

const CheckoutCancel = () => {
  const [searchParams] = useSearchParams()
  const { getOrder } = useOrders()
  const orderId = searchParams.get('order')
  const orderNumber = searchParams.get('order_number')
  const displayOrderNumber = shortenOrderNumber(orderNumber)
  const [loading, setLoading] = useState(true)
  const [order, setOrder] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    const loadOrder = async () => {
      const token = localStorage.getItem('token')
      if (!orderId || !token) {
        if (active) {
          setLoading(false)
        }
        return
      }

      try {
        const payload = await getOrder(orderId)
        if (active) {
          setOrder(payload.data || null)
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message)
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    loadOrder()
    return () => {
      active = false
    }
  }, [getOrder, orderId])

  const paymentWasConfirmed = order?.payment_status === 'paid'

  return (
    <Box sx={{ minHeight: '80vh', bgcolor: theme.palette.secondary.main, padding: '40px 0' }}>
      <Box sx={{ maxWidth: 1100, margin: '0 auto', paddingX: 2 }}>
        <Stack spacing={3}>
          <Paper elevation={8} sx={{ padding: { xs: '24px', md: '32px' }, borderRadius: '18px' }}>
            <Stack spacing={2}>
              <Typography variant='h4' sx={{ fontSize: { xs: '26px', md: '32px' }, fontWeight: 700 }}>
                {paymentWasConfirmed ? 'Plata a fost deja confirmată' : 'Plata nu a fost finalizată'}
              </Typography>
              <Typography variant='body1'>
                {paymentWasConfirmed
                  ? 'Comanda ta este deja plătită. O poți urmări în contul tău.'
                  : 'Comanda a rămas salvată în contul tău. Poți reveni în coș și relua plata atunci când ești pregătit.'}
              </Typography>
              {orderNumber && (
                <Stack spacing={0.25}>
                  <Typography variant='body1'>
                    <strong>Număr comandă:</strong> {displayOrderNumber}
                  </Typography>
                  {hasShortDisplayVariant(orderNumber) && (
                    <Typography variant='caption' sx={{ color: theme.palette.fontColor.medium }}>
                      Ref completă: {orderNumber}
                    </Typography>
                  )}
                </Stack>
              )}
              {!paymentWasConfirmed && (
                <Alert severity='info'>
                  Coșul rămâne disponibil, iar comanda poate fi reluată fără să pierzi produsele selectate.
                </Alert>
              )}
              {error && <Alert severity='warning'>{error}</Alert>}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <PublicLink to='/cosulmeu'>
                  <Button variant='contained' color='accent' sx={{ color: 'white', borderRadius: '18px' }}>
                    Înapoi la coș
                  </Button>
                </PublicLink>
                <PublicLink to='/profilulmeu#Orders'>
                  <Button variant='outlined' color='accent' sx={{ borderRadius: '18px' }}>
                    Vezi comenzile mele
                  </Button>
                </PublicLink>
              </Stack>
            </Stack>
          </Paper>

          {loading
            ? (
              <Paper elevation={3} sx={{ padding: '24px', borderRadius: '18px' }}>
                <Typography>Se încarcă sumarul comenzii…</Typography>
              </Paper>
              )
            : order
              ? <OrderSummaryCard order={order} onOrderUpdated={setOrder} />
              : null}
        </Stack>
      </Box>
    </Box>
  )
}

export default CheckoutCancel
